import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, tokenManager } from '../utils/api';

// Query keys
const HOW_TO_APPLY_QUERY_KEY = 'howToApply';

// Custom hook for fetching how-to-apply data
export const useHowToApply = (page = 1, limit = 10) => {
  return useQuery({
    queryKey: [HOW_TO_APPLY_QUERY_KEY, page, limit],
    queryFn: () => apiClient.get('how-to-apply/student/data', { page, limit }),
    keepPreviousData: true, // Keep previous data while fetching new data
    staleTime: 2 * 60 * 1000, // 2 minutes
    select: (data) => {
      // Transform the API response to match the expected format
      return {
        data: data.data?.data || [],
        total: data.data?.total || 0,
        page: data.data?.page || 1,
        limit: data.data?.limit || 10,
        has_next: data.data?.has_next || false,
        has_prev: data.data?.has_prev || false,
        pagination: {
          current: data.data?.page || 1,
          pageSize: data.data?.limit || 10,
          total: data.data?.total || 0
        }
      };
    }
  });
};

// Custom hook for creating how-to-apply data
export const useCreateHowToApply = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (formData) => {
      const response = await fetch('https://backend.hellolynx.io/dashboard/how-to-apply/student/data', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
          // Don't set Content-Type header for FormData, let browser set it
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create item');
      }
      
      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch how-to-apply data
      queryClient.invalidateQueries({ queryKey: [HOW_TO_APPLY_QUERY_KEY] });
    },
  });
};

// Custom hook for updating how-to-apply data
export const useUpdateHowToApply = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data: formData }) => {
      const response = await fetch(`https://backend.hellolynx.io/dashboard/how-to-apply/student/data/${id}`, {
        method: 'POST', // Laravel uses POST with _method=PUT for FormData
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
          // Don't set Content-Type header for FormData, let browser set it
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update item');
      }
      
      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch how-to-apply data
      queryClient.invalidateQueries({ queryKey: [HOW_TO_APPLY_QUERY_KEY] });
    },
  });
};

// Custom hook for deleting how-to-apply data
export const useDeleteHowToApply = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => apiClient.delete(`how-to-apply/student/data/${id}`),
    onSuccess: () => {
      // Invalidate and refetch how-to-apply data
      queryClient.invalidateQueries({ queryKey: [HOW_TO_APPLY_QUERY_KEY] });
    },
  });
};

// Custom hook for getting a single how-to-apply item
export const useHowToApplyItem = (id) => {
  return useQuery({
    queryKey: [HOW_TO_APPLY_QUERY_KEY, id],
    queryFn: () => apiClient.get(`how-to-apply/student/data/${id}`),
    enabled: !!id, // Only run query if id exists
  });
}; 